import moment from 'moment';

// Not in generated __gqltypes__ until codegen runs against a backend that has the deeplink schema
export enum DeepLinkTarget {
  HOME = 'HOME',
  SHOP = 'SHOP',
  SHOP_REWARDS = 'SHOP_REWARDS',
  SHOP_GACHA = 'SHOP_GACHA',
  MUSIC = 'MUSIC',
  TEAM_SEARCH = 'TEAM_SEARCH',
  PLUS_SUBSCRIPTION = 'PLUS_SUBSCRIPTION',
  TOURNAMENT = 'TOURNAMENT',
  LIVE_SHOW = 'LIVE_SHOW',
  TUNEOLOGY = 'TUNEOLOGY',
  TURN_QUIZ_BY_CONTENT_PACK = 'TURN_QUIZ_BY_CONTENT_PACK',
  REDEEM = 'REDEEM',
  MARATHON = 'MARATHON',
  FESTIVAL = 'FESTIVAL',
  MONTHLY_PASS = 'MONTHLY_PASS',
}

export type DeepLinkParams = {
  gachaBoxId?: string | null;
  contentPackId?: string | null;
  redeemCode?: string | null;
};

export type DeepLinkItem = {
  id: string;
  url: string;
  name: string;
  campaign: string | null;
  target: DeepLinkTarget;
  params: DeepLinkParams | null;
  fallbackTarget: DeepLinkTarget | null;
  enabled: boolean;
  endDate: number | null;
  maxUses: number | null;
  createdBy: string | null;
  stats: { uniqueUsers: number; totalOpens: number };
  created: number;
  updatedAt: number;
};

export type DeepLinkParamKey = keyof DeepLinkParams;

type TargetInfo = {
  label: string;
  param?: { key: DeepLinkParamKey; label: string; required: boolean; placeholder: string; help: string };
};

export const TARGET_INFO: Record<DeepLinkTarget, TargetInfo> = {
  [DeepLinkTarget.HOME]: { label: 'Home' },
  [DeepLinkTarget.SHOP]: { label: 'Shop (IAP)' },
  [DeepLinkTarget.SHOP_REWARDS]: { label: 'Shop · Rewards' },
  [DeepLinkTarget.SHOP_GACHA]: {
    label: 'Shop · Gacha (Surprise Boxes)',
    param: {
      key: 'gachaBoxId',
      label: 'Gacha box ID (optional)',
      required: false,
      placeholder: 'Leave empty to open the Gacha Shop',
      help: 'Box to select when the Gacha Shop opens.',
    },
  },
  [DeepLinkTarget.MUSIC]: { label: 'Music' },
  [DeepLinkTarget.TEAM_SEARCH]: { label: 'Team search' },
  [DeepLinkTarget.PLUS_SUBSCRIPTION]: { label: 'Plus subscription offer' },
  [DeepLinkTarget.TOURNAMENT]: { label: 'Tournament' },
  [DeepLinkTarget.LIVE_SHOW]: { label: 'Next live show' },
  [DeepLinkTarget.TUNEOLOGY]: { label: 'Tuneology' },
  [DeepLinkTarget.MARATHON]: { label: 'Marathon' },
  [DeepLinkTarget.FESTIVAL]: { label: 'Festival' },
  [DeepLinkTarget.MONTHLY_PASS]: { label: 'Monthly Pass' },
  [DeepLinkTarget.TURN_QUIZ_BY_CONTENT_PACK]: {
    label: 'Quiz on a content pack',
    param: {
      key: 'contentPackId',
      label: 'Content pack ID',
      required: true,
      placeholder: 'e.g. 3f2a…',
      help: 'The backend checks that this content pack exists.',
    },
  },
  [DeepLinkTarget.REDEEM]: {
    label: 'Redeem code',
    param: {
      key: 'redeemCode',
      label: 'Redeem code',
      required: true,
      placeholder: 'e.g. WELCOME2026',
      help: 'Opens the redeem screen with this code filled in. Uppercase letters and digits only.',
    },
  },
};

// A fallback is opened without params, so targets that need one cannot be used
export const FALLBACK_TARGETS = Object.values(DeepLinkTarget).filter((target) => !TARGET_INFO[target].param?.required);

export const REDEEM_CODE_REGEX = /^[A-Z0-9]+$/;

export type DeepLinkStatusInfo = { label: 'Active' | 'Disabled' | 'Expired'; variant: string };

export const getDeepLinkStatus = (deepLink: Pick<DeepLinkItem, 'enabled' | 'endDate'>): DeepLinkStatusInfo => {
  if (!deepLink.enabled) {
    return { label: 'Disabled', variant: 'dark' };
  }
  if (deepLink.endDate && moment(deepLink.endDate).isBefore(moment())) {
    return { label: 'Expired', variant: 'secondary' };
  }
  return { label: 'Active', variant: 'success' };
};

export const formatTarget = (deepLink: Pick<DeepLinkItem, 'target' | 'params'>): string => {
  const info = TARGET_INFO[deepLink.target];
  const paramValue = info?.param ? deepLink.params?.[info.param.key] : null;
  return paramValue ? `${info.label}: ${paramValue}` : info?.label ?? deepLink.target;
};

export const ERROR_MESSAGES: Record<string, string> = {
  DEEP_LINK_PARAM_REQUIRED: 'This screen needs a value.',
  DEEP_LINK_CONTENT_PACK_NOT_FOUND: 'No content pack has this ID.',
  DEEP_LINK_INVALID_REDEEM_CODE: 'The redeem code may only contain uppercase letters and digits.',
  DEEP_LINK_INVALID_FALLBACK_TARGET: 'This fallback screen needs a value, so it cannot be used as a fallback.',
  DEEP_LINK_NAME_REQUIRED: 'A name is required.',
  DEEP_LINK_INVALID_MAX_USES: 'Max players must be a whole number of at least 1.',
  DEEP_LINK_NOT_FOUND: 'This link no longer exists.',
};

export const formatDeepLinkError = (error: { message?: string } | null | undefined): string => {
  const code = Object.keys(ERROR_MESSAGES).find((key) => error?.message?.includes(key));
  return code ? ERROR_MESSAGES[code] : error?.message ?? 'unknown error';
};
